import { onlineManager, useSuspenseQueries } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import z from 'zod/v4'
import { parse } from 'date-fns'
import type { GetBookingTransportationResponse } from '@/types/booking'
import { queryKeys } from '@/queries/keys'
import {
  getBookingTransportationQueryOptions,
  getListTimeQueryOptions,
  getListTimeSlotQueryOptions,
  getListTimeTypesQueryOptions,
  getMyBookingDetailQueryOptions,
} from '@/queries/query'
import { PageWithRedirectSearchSchema } from '@/schemas/page'
import { toNearestTimeSlot } from '@/utils/date'
import TimePage from '@/pages/make-booking/bus-details/Time'

export const Route = createFileRoute(
  '/make-booking/$id/bus-details/$transportationId/time/',
)({
  validateSearch: z.object({
    ...PageWithRedirectSearchSchema.shape,
    time_slot: z.string().optional(),
  }),
  loaderDeps: ({ search }) => ({
    time_slot: search.time_slot,
  }),
  loader: async ({ context, params, deps }) => {
    const transportation =
      onlineManager.isOnline() && !context.isRestoring
        ? await context.queryClient.ensureQueryData(
            getBookingTransportationQueryOptions(params.transportationId),
          )
        : context.queryClient.getQueryData<GetBookingTransportationResponse>(
            queryKeys.booking.transportation(params.transportationId),
          )
    const timeSlot = deps.time_slot
      ? deps.time_slot
      : toNearestTimeSlot(
          transportation?.data?.booked_transportation?.departure_date
            ? parse(
                transportation.data.booked_transportation.departure_date,
                'yyyy-MM-dd',
                new Date(),
              )
            : undefined,
        )
    if (onlineManager.isOnline() && !context.isRestoring) {
      return [
        transportation,
        context.queryClient.ensureQueryData(
          getListTimeQueryOptions({
            id: params.transportationId,
          }),
        ),
        context.queryClient.ensureQueryData(
          getListTimeTypesQueryOptions({
            id: params.transportationId,
            time_slot: timeSlot,
          }),
        ),
        context.queryClient.ensureQueryData(
          getListTimeSlotQueryOptions({
            id: params.transportationId,
            time_slot: timeSlot,
          }),
        ),
      ]
    }
    return [
      transportation,
      context.queryClient.getQueryData(
        queryKeys.booking.time({
          id: params.transportationId,
        }),
      ),
      context.queryClient.getQueryData(
        queryKeys.booking.timeTypes({
          id: params.transportationId,
          time_slot: timeSlot,
        }),
      ),
      context.queryClient.getQueryData(
        queryKeys.booking.timeSlot({
          id: params.transportationId,
          time_slot: timeSlot,
        }),
      ),
    ]
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { id, transportationId } = Route.useParams()
  const [
    {
      data: {
        data: { booking },
      },
    },
    {
      data: {
        data: { booked_transportation: transportation },
      },
    },
  ] = useSuspenseQueries({
    queries: [
      getMyBookingDetailQueryOptions(id),
      getBookingTransportationQueryOptions(transportationId),
    ],
  })
  return <TimePage booking={booking} transportation={transportation} />
}
